import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: null = checking, false = not authed, object = authed
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // 2FA-gated login: server returns a tmp_token instead of the user record.
      if (data && data.requires_2fa) {
        return { ok: true, requires_2fa: true, tmp_token: data.tmp_token };
      }
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const twofaLogin = useCallback(async (tmp_token, code) => {
    try {
      const { data } = await api.post("/auth/2fa/login", { tmp_token, code });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Best-effort logout: server may be unreachable but we still clear local state.
      // Surfacing to console aids debugging without leaking to the user.
      console.warn("Logout request failed (cookies will still be cleared locally):", err?.message || err);
    }
    setUser(false);
  }, []);

  const acceptInvite = useCallback(async ({ token, password, name }) => {
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password, name });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  // Used by the change-password flow: the server already cleared the cookies
  // and bumped ``password_changed_at`` (invalidating the JWT). Just drop the
  // local user reference so ProtectedRoute redirects to /login.
  const signOutLocal = useCallback(() => {
    setUser(false);
  }, []);

  // Memoize context value to avoid forcing every consumer to re-render on each
  // AuthProvider render. Reference stability matters for downstream useEffect deps.
  const value = useMemo(
    () => ({ user, login, twofaLogin, logout, acceptInvite, refresh: fetchMe, signOutLocal }),
    [user, login, twofaLogin, logout, acceptInvite, fetchMe, signOutLocal]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
